import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { MaskedLine, FadeUp, Kicker } from "@/components/Reveal";

export default function Portfolio() {
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Lavori — Luca Mannu Giardini";
    api.get("/works").then((r) => setWorks(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <main className="pt-32" data-testid="portfolio-page">
      <header className="mx-auto max-w-7xl px-6 lg:px-10">
        <Kicker>Il portfolio</Kicker>
        <h1 className="mt-4 font-serif text-5xl tracking-tight text-forest md:text-6xl">
          <MaskedLine delay={0.1}>Giardini che</MaskedLine>
          <MaskedLine delay={0.22} className="italic text-moss">raccontano storie.</MaskedLine>
        </h1>
        <FadeUp delay={0.35}>
          <p className="mt-6 max-w-xl leading-relaxed text-sage">
            Una selezione dei progetti realizzati tra Cagliari e la costa sud. Ogni giardino è un dialogo tra il luogo e chi lo vive.
          </p>
        </FadeUp>
      </header>

      <section className="mx-auto max-w-7xl px-6 pb-32 pt-20 lg:px-10">
        {loading && <p className="text-sage" data-testid="portfolio-loading">Caricamento lavori…</p>}
        {!loading && works.length === 0 && <p className="text-sage" data-testid="portfolio-empty">Nessun lavoro pubblicato ancora.</p>}
        <div className="flex flex-col gap-24 lg:gap-36">
          {works.map((w, i) => (
            <Link key={w.id} to={`/lavori/${w.id}`} className="group grid items-end gap-10 md:grid-cols-12" data-testid={`work-${w.id}`}>
              <FadeUp className={i % 2 ? "md:col-span-7 md:order-2" : "md:col-span-7"}>
                <div className={`group overflow-hidden bg-sand ${i % 2 ? "arch-frame" : "rounded-sm"}`}>
                  <img
                    src={w.image}
                    alt={w.title}
                    loading="lazy"
                    className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i % 2 ? "aspect-[4/5]" : "aspect-[16/11]"}`}
                  />
                </div>
              </FadeUp>
              <FadeUp delay={0.15} className={i % 2 ? "md:col-span-5 md:order-1" : "md:col-span-5"}>
                <span className="font-serif text-6xl italic text-line">0{i + 1}</span>
                <h2 className="mt-2 font-serif text-3xl tracking-tight text-forest md:text-4xl">{w.title}</h2>
                <p className="mt-3 flex items-center gap-2 font-accent text-xs uppercase tracking-[0.2em] text-terra">
                  <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} /> {w.location} · {w.year}
                </p>
                <p className="mt-6 max-w-md leading-relaxed text-sage">{w.description}</p>
                <span className="link-underline mt-4 inline-block font-accent text-xs uppercase tracking-[0.2em] text-terra">Scopri il progetto</span>
              </FadeUp>
            </Link>
          ))}
        </div>

        <FadeUp className="mt-32 border-t border-line pt-16 text-center">
          <h2 className="mx-auto max-w-2xl font-serif text-3xl leading-snug tracking-tight text-forest md:text-4xl">
            Il prossimo giardino potrebbe essere <span className="italic text-terra">il tuo.</span>
          </h2>
          <Link to="/contatti" data-testid="portfolio-quote-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-forest px-10 py-4 font-accent text-sm uppercase tracking-[0.2em] text-bone transition-transform duration-300 hover:scale-105 active:scale-95">
            Richiedi un preventivo <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </FadeUp>
      </section>
    </main>
  );
}
